import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/api';

interface Announcement {
    id: string;
    message: string;
    createdAt: string;
}

const AnnouncementBanner: React.FC = () => {
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const [isLiveActive, setIsLiveActive] = useState(() => !!localStorage.getItem('is_live_stream_active'));
    const location = useLocation();

    // Check hidden paths
    const hiddenPaths = ['/welcome', '/maintenance', '/login', '/register', '/forgot-password'];
    const shouldHide = hiddenPaths.some(path => location.pathname.startsWith(path));

    useEffect(() => {
        fetchAnnouncement();
        // Poll every 60 seconds for updates
        const interval = setInterval(fetchAnnouncement, 60000);

        // Listen for live stream status changes
        const handleLiveStatus = (e: any) => {
            setIsLiveActive(e.detail?.active || !!localStorage.getItem('is_live_stream_active'));
        };

        window.addEventListener('livestream:status', handleLiveStatus);

        return () => {
            clearInterval(interval);
            window.removeEventListener('livestream:status', handleLiveStatus);
        };
    }, []);

    const fetchAnnouncement = async () => {
        try {
            const res = await fetch(`${getApiBaseUrl()}/announcements/active`);
            const data = await res.json();
            if (data.announcement) {
                setAnnouncement(data.announcement);
                // Reset dismissed state when new announcement appears
                const storedId = localStorage.getItem('dismissed_announcement_id');
                if (storedId !== data.announcement.id) {
                    setDismissed(false);
                }
            } else {
                setAnnouncement(null);
            }
        } catch (error) {
            console.error('Failed to fetch announcement:', error);
        }
    };

    const handleDismiss = () => {
        if (announcement) {
            localStorage.setItem('dismissed_announcement_id', announcement.id);
            setDismissed(true);
        }
    };

    if (!announcement || dismissed || isLiveActive || shouldHide) {
        return null;
    }

    return (
        <div className="relative group bg-gradient-to-r from-blue-900 via-blue-800 to-blue-600 dark:from-[#1a1e3a] dark:to-[#1a1e3a] dark:bg-[#1a1e3a] border-b border-blue-800/50 dark:border-gray-800 shadow-md z-[100] transition-colors duration-200">
            <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-4 py-2 px-4 sm:px-6 relative z-10">
                {/* Icon Label */}
                <div className="hidden sm:flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-white">Thông báo</span>
                </div>

                {/* Mobile Icon */}
                <div className="sm:hidden flex-shrink-0 text-white">
                    <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                </div>

                {/* Scrolling message */}
                <div className="flex-1 overflow-hidden group cursor-default mask-linear-fade">
                    <div className="marquee-track group-hover:[animation-play-state:paused]">
                        {/* Group 1 */}
                        <div className="marquee-group">
                            {Array(8).fill(null).map((_, i) => (
                                <span key={`g1-${i}`} className="font-medium text-white/90 text-sm sm:text-base tracking-wide flex items-center shrink-0">
                                    {announcement?.message}
                                    <span className="w-1.5 h-1.5 rounded-full bg-white/50 mx-4 sm:mx-8"></span>
                                </span>
                            ))}
                        </div>
                        {/* Group 2 (Duplicate for seamless loop) */}
                        <div className="marquee-group">
                            {Array(8).fill(null).map((_, i) => (
                                <span key={`g2-${i}`} className="font-medium text-white/90 text-sm sm:text-base tracking-wide flex items-center shrink-0">
                                    {announcement?.message}
                                    <span className="w-1.5 h-1.5 rounded-full bg-white/50 mx-4 sm:mx-8"></span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Dismiss button */}
                <button
                    onClick={handleDismiss}
                    className="flex-shrink-0 p-1 rounded-full transition-all duration-200 text-white/50 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 z-10 absolute right-2 sm:right-4"
                    title="Đóng thông báo"
                    style={{ minWidth: '24px', minHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <style>{`
        .mask-linear-fade {
          mask-image: linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent);
        }

        .marquee-track {
          display: flex;
          width: max-content;
          animation: marquee 60s linear infinite;
        }

        .marquee-group {
            display: flex;
            align-items: center;
        }

        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
        </div>
    );
};

export default AnnouncementBanner;
